/* assets/bm-local-delivery.js
 *
 * 1. On page load, work out the customer's ZIP:
 *      saved ZIP (typed by the customer, or auto-detected < 24h ago)
 *      -> GPS, only if the browser already has permission (no prompt)
 *      -> IP address lookup
 * 2. Check it against window.BM_FREE_LOCAL_DELIVERY_ZIPS
 * 3. Set  <html data-ld-eligible="true|false|pending">  -> CSS switches EVERY label on the page,
 *    including labels that are injected later by JS (no re-binding needed).
 *    <html data-ld-source="user|auto"> says who supplied the ZIP (used by the inline ZIP widget).
 * 4. Clicking any [data-ld-open] label opens the ZIP popup.
 * 5. Any number of inline ZIP widgets ([data-ld-zipcheck], snippet ld-zipcheck.liquid) share the same state.
 *    They always stay on the input box and are prefilled with the ZIP from location / IP (or the one the
 *    customer typed earlier). "Check" opens the popup on its success / failed screen, and the box turns
 *    green when the ZIP is eligible (stays blue otherwise).
 * 7. <html data-ld-ship-filter> is set while a Free Shipping filter (Freight_free / Economy_free) is in the URL
 *    WITHOUT the Local Delivery filter, so listing pages keep showing "Free Shipping" instead of swapping to
 *    Free Local Delivery. If the Local filter is applied as well, Free Local Delivery is prioritized.
 * 6. If the ZIP is known and NOT eligible (typed by the customer OR detected on page load from location / IP),
 *    the LocalDelivery_free filter is removed from the URL and the page reloads. See FILTER_RULES:
 *    /collections -> filter.p.tag, /search -> filter.p.tag and pf_t_shipping (Boost).
 */
(function () {
	"use strict";
	if (window.__bmLocalDelivery) return;

	var STORAGE_KEY = "bm_delivery_zip";
	var USER_TTL = 30 * 24 * 60 * 60 * 1000; // ZIP typed by the customer: 30 days
	var AUTO_TTL = 24 * 60 * 60 * 1000;      // ZIP detected automatically: 1 day
	var PENDING_TIMEOUT = 3000;              // stop hiding the label after 3s if detection is slow

	// When the ZIP is NOT eligible, remove the "Free Local Delivery" filter from the URL and reload.
	var FILTER_VALUE = "LocalDelivery_free";
	var FILTER_RULES = [
		{ path: "/collections", param: "filter.p.tag" }, // Shopify collection filters
		{ path: "/search", param: "filter.p.tag" },      // Shopify search filters
		{ path: "/search", param: "pf_t_shipping" },     // Boost search filters
	];
	var STRIP_ON_AUTO = true;                 // true = also strip when the ZIP detected on page load (location / IP) is outside the area
	var STRIP_GUARD_KEY = "bm_ld_filter_strip";
	var STRIP_GUARD_MS = 5000;                // never strip/reload twice within 5s (prevents reload loops)

	// A Free Shipping filter in the URL (without the Local filter) beats the "local delivery wins" label swap
	// (see ld-shipping-label.liquid).
	// Sets <html data-ld-ship-filter> so CSS can keep showing "Free Shipping" on filtered listing pages,
	// including lists that are re-rendered by JS (Boost / AJAX filters) after the filter changes the URL.
	var SHIP_FILTER_PARAMS = ["filter.p.tag", "pf_t_shipping"];
	var SHIP_FILTER_VALUES = ["Freight_free", "Economy_free"];

	var root = document.documentElement;
	var state = { zip: null, eligible: false, source: null };

	/* ---------------------------------------------------------------------
	 * ZIP helpers
	 * ------------------------------------------------------------------- */
	function normalizeZip(z) {
		return String(z == null ? "" : z).trim().split("-")[0].slice(0, 5); // handles ZIP+4
	}
	function isValidZip(z) {
		return /^\d{5}$/.test(z);
	}
	function pickZip(raw) {
		var z = normalizeZip(raw);
		return isValidZip(z) ? z : null;
	}

	// Swap this for an app-proxy / API call if the list ever gets too big for the page.
	function isFreeLocalDeliveryZip(zip) {
		var z = normalizeZip(zip);
		if (!isValidZip(z)) return false;
		var list = window.BM_FREE_LOCAL_DELIVERY_ZIPS || [];
		return list.some(function (x) { return normalizeZip(x) === z; });
	}

	/* ---------------------------------------------------------------------
	 * Storage
	 * ------------------------------------------------------------------- */
	function readSaved() {
		try {
			var s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
			if (!s || !isValidZip(normalizeZip(s.zip))) return null;
			var ttl = s.source === "user" ? USER_TTL : AUTO_TTL;
			return Date.now() - s.at < ttl ? s : null;
		} catch (e) {
			return null;
		}
	}
	function writeSaved(zip, source) {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ zip: zip, source: source, at: Date.now() }));
		} catch (e) { /* storage blocked: fine, we just re-detect next time */ }
	}

	/* ---------------------------------------------------------------------
	 * ZIP detection (GPS -> IP)
	 * ------------------------------------------------------------------- */
	function fetchJSON(url) {
		return fetch(url)
			.then(function (r) { return r.ok ? r.json() : null; })
			.catch(function () { return null; });
	}

	function getPosition(options) {
		return new Promise(function (resolve, reject) {
			navigator.geolocation.getCurrentPosition(
				function (pos) { resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
				reject,
				options
			);
		});
	}

	async function getZipFromCoords(lat, lng) {
		// Primary: BigDataCloud client-side reverse geocode (no key)
		var d = await fetchJSON(
			"https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=" + lat + "&longitude=" + lng + "&localityLanguage=en"
		);
		var z = d && pickZip(d.postcode);
		if (z) return z;

		// Fallback: OpenStreetMap Nominatim (light use only)
		d = await fetchJSON(
			"https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=" + lat + "&lon=" + lng
		);
		return d && d.address ? pickZip(d.address.postcode) : null;
	}

	async function getZipFromIP() {
		var d = await fetchJSON("https://ipapi.co/json/");
		var z = d && pickZip(d.postal);
		if (z) return z;

		d = await fetchJSON("https://ipwho.is/");
		return d && d.success !== false ? pickZip(d.postal) : null;
	}

	async function detectZip() {
		var zip = null;

		// GPS only if the user has already allowed it (no permission prompt on page load)
		if (navigator.permissions && navigator.geolocation) {
			try {
				var p = await navigator.permissions.query({ name: "geolocation" });
				if (p.state === "granted") {
					var pos = await getPosition({ timeout: 8000, maximumAge: 300000 });
					zip = await getZipFromCoords(pos.lat, pos.lng);
				}
			} catch (e) { /* fall through to IP */ }
		}

		// if (!zip) zip = await getZipFromIP();
		return zip;
	}

	/* ---------------------------------------------------------------------
	 * Page state
	 * ------------------------------------------------------------------- */
	function applyState(zip, eligible, source) {
		state.zip = zip || null;
		state.eligible = !!eligible;
		state.source = source || null;

		root.setAttribute("data-ld-eligible", state.eligible ? "true" : "false");
		if (state.source) root.setAttribute("data-ld-source", state.source);
		else root.removeAttribute("data-ld-source");
		if (state.zip) root.setAttribute("data-ld-zip", state.zip);
		else root.removeAttribute("data-ld-zip");

		document.dispatchEvent(new CustomEvent("ld:change", {
			detail: { zip: state.zip, eligible: state.eligible, source: state.source },
		}));

		syncZipInputs();
		stripLocalDeliveryFilter();
	}

	/* ---------------------------------------------------------------------
	 * Remove the Free Local Delivery filter from the URL when the ZIP isn't eligible
	 * ------------------------------------------------------------------- */
	// Pure helper: returns the new URL string, or null when nothing needs to change.
	function getStrippedFilterUrl(href) {
		var url;
		try { url = new URL(href); } catch (e) { return null; }

		var changed = false;
		FILTER_RULES.forEach(function (rule) {
			if (url.pathname.indexOf(rule.path) === -1) return;
			var values = url.searchParams.getAll(rule.param);
			if (values.indexOf(FILTER_VALUE) === -1) return;

			// drop only LocalDelivery_free, keep any other values of the same filter
			url.searchParams.delete(rule.param);
			values.forEach(function (v) { if (v !== FILTER_VALUE) url.searchParams.append(rule.param, v); });
			changed = true;
		});

		if (!changed) return null;
		url.searchParams.delete("page"); // result set changes, so go back to page 1
		return url.toString();
	}

	function stripLocalDeliveryFilter() {
		if (state.eligible || !state.zip) return;                         // only when we KNOW the ZIP is outside the area
		if (state.source !== "user" && !STRIP_ON_AUTO) return;
		if (popup && popup.isOpen()) return;                              // let the customer read the result; close() calls us again

		var next = getStrippedFilterUrl(window.location.href);
		if (!next) return;

		try {
			var last = Number(sessionStorage.getItem(STRIP_GUARD_KEY)) || 0;
			if (Date.now() - last < STRIP_GUARD_MS) return;
			sessionStorage.setItem(STRIP_GUARD_KEY, String(Date.now()));
		} catch (e) { /* storage blocked: still fine, URL no longer has the filter after the reload */ }

		window.location.replace(next); // same as a reload, but on the URL without the filter
	}

	// Validate + save + apply a ZIP typed by the customer (popup, inline widgets, public API)
	function commitZip(raw) {
		var z = pickZip(raw);
		if (!z) return { valid: false, zip: null, eligible: false };
		var ok = isFreeLocalDeliveryZip(z);
		writeSaved(z, "user");
		applyState(z, ok, "user");
		return { valid: true, zip: z, eligible: ok };
	}

	function initState() {
		var saved = readSaved();
		if (saved) {
			var z = normalizeZip(saved.zip);
			applyState(z, isFreeLocalDeliveryZip(z), saved.source === "user" ? "user" : "auto");
			return;
		}

		// Nothing saved: hide the label briefly so it doesn't flash the wrong state
		root.setAttribute("data-ld-eligible", "pending");
		var timer = setTimeout(function () {
			if (root.getAttribute("data-ld-eligible") === "pending") applyState(null, false, null);
		}, PENDING_TIMEOUT);

		detectZip().then(function (zip) {
			clearTimeout(timer);
			if (state.source === "user") return; // customer already typed a ZIP, theirs wins
			if (zip) {
				writeSaved(zip, "auto");
				applyState(zip, isFreeLocalDeliveryZip(zip), "auto");
			} else if (root.getAttribute("data-ld-eligible") === "pending") {
				applyState(null, false, null);
			}
		});
	}

	/* ---------------------------------------------------------------------
	 * Popup
	 * ------------------------------------------------------------------- */
	var $ = function (id) { return document.getElementById(id); };
	var popup = null; // set in setupPopup()

	function setupPopup() {
		var overlay = $("ld-overlay");
		if (!overlay) return;

		var modal = $("ld-modal");
		var form = $("ld-st-enter");
		var zipEl = $("ld-zip");
		var errBox = $("ld-zip-err");
		var errText = $("ld-zip-err-text");
		var lastFocus = null;
		var prevOverflow = "";

		function show(id) {
			["ld-st-enter", "ld-st-ok", "ld-st-no"].forEach(function (s) { $(s).hidden = s !== id; });
		}
		function setError(msg) {
			errText.textContent = msg;
			errBox.hidden = false;
			zipEl.classList.add("is-error");
			zipEl.setAttribute("aria-invalid", "true");
		}
		function clearError() {
			errBox.hidden = true;
			zipEl.classList.remove("is-error");
			zipEl.removeAttribute("aria-invalid");
		}

		function openOverlay(trigger) {
			if (window.__tooltipManager) window.__tooltipManager.hide();
			if (overlay.hidden) { // guard: don't capture an already-locked overflow value
				lastFocus = trigger || document.activeElement;
				prevOverflow = document.body.style.overflow;
				document.body.style.overflow = "hidden";
				overlay.hidden = false;
			}
		}

		// Normal open: the "enter your ZIP" screen
		function open(trigger) {
			openOverlay(trigger);
			show("ld-st-enter");
			clearError();
			if (state.zip) zipEl.value = state.zip;
			setTimeout(function () { zipEl.focus(); zipEl.select(); }, 30);
		}

		// Straight to the success / failed screen for a ZIP (used by the inline widget).
		// The overlay opens FIRST, so an ineligible ZIP can't reload the page underneath it.
		function openWithZip(raw, trigger) {
			if (!pickZip(raw)) return false;
			openOverlay(trigger);
			clearError();
			submit(raw);
			return true;
		}

		function close() {
			overlay.hidden = true;
			document.body.style.overflow = prevOverflow;
			if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
			stripLocalDeliveryFilter(); // ZIP was ineligible while the popup was open: now remove the filter
		}

		function submit(raw) {
			var res = commitZip(raw);
			if (!res.valid) {
				setError("Please enter a valid 5-digit ZIP code.");
				zipEl.focus();
				return;
			}
			clearError();

			var ok = res.eligible;
			zipEl.value = res.zip;

			if (ok) { $("ld-ok-zip").textContent = res.zip; show("ld-st-ok"); }
			else { $("ld-no-zip").textContent = res.zip; show("ld-st-no"); }

			var first = $(ok ? "ld-st-ok" : "ld-st-no").querySelector("button");
			if (first) first.focus();
		}

		zipEl.addEventListener("input", function () {
			zipEl.value = zipEl.value.replace(/\D/g, "").slice(0, 5);
			if (!errBox.hidden && zipEl.value.length === 5) clearError();
		});

		form.addEventListener("submit", function (e) {
			e.preventDefault();
			submit(zipEl.value);
		});

		overlay.addEventListener("click", function (e) {
			if (e.target === overlay || e.target.closest("[data-ld-close]")) close();
			else if (e.target.closest("[data-ld-change]")) { show("ld-st-enter"); zipEl.focus(); zipEl.select(); }
		});

		document.addEventListener("keydown", function (e) {
			if (overlay.hidden) return;
			if (e.key === "Escape") { close(); return; }
			if (e.key === "Tab") { // simple focus trap
				var f = Array.prototype.filter.call(modal.querySelectorAll("button, input"), function (el) {
					return el.offsetParent !== null && !el.disabled;
				});
				if (!f.length) return;
				var first = f[0], last = f[f.length - 1];
				if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
				else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
			}
		});

		popup = { open: open, close: close, openWithZip: openWithZip, isOpen: function () { return !overlay.hidden; } };
	}

	/* ---------------------------------------------------------------------
	 * Inline ZIP check widgets  (snippet: ld-zipcheck.liquid, root = [data-ld-zipcheck])
	 *  - any number of copies on the page (sidebar, filter drawer, PDP, cart...)
	 *  - copies added later (AJAX filter / sidebar re-render) are enhanced automatically
	 *  - handlers sit on each widget, so input/change/Enter never reach a surrounding filter <form>
	 *  - the widget never switches views; its colour (blue / green) is pure CSS, driven by
 *    <html data-ld-source / data-ld-eligible>. The result is shown in the popup instead.
	 * ------------------------------------------------------------------- */
	var ZC_SEL = "[data-ld-zipcheck]";

	// Show the current ZIP (typed by the customer, or detected from location / IP on page load) in every widget input.
	// A ZIP detected automatically never overwrites something the customer is typing or has typed.
	// Call it with no argument when the ZIP STATE changes (applyState), or with one widget to initialise a new copy.
	// It must NOT run on unrelated DOM changes: that used to wipe a ZIP the customer was typing.
	function syncZipInputs(onlyWrap) {
		if (!state.zip) return;
		var inputs = (onlyWrap || document).querySelectorAll("[data-ld-zc-input]");
		inputs.forEach(function (input) {
			if (input === document.activeElement) return; // don't overwrite while they are typing
			var wrap = input.closest(ZC_SEL);
			if (state.source !== "user" && wrap && wrap.hasAttribute("data-dirty")) return;
			input.value = state.zip;
			if (wrap) wrap.removeAttribute("data-dirty");
		});
	}

	function enhanceZipCheck(wrap) {
		if (wrap.__ldZc) return;
		wrap.__ldZc = true;

		var input = wrap.querySelector("[data-ld-zc-input]");
		var err = wrap.querySelector("[data-ld-zc-error]");
		var btn = wrap.querySelector("[data-ld-zc-submit]");
		if (!input || !btn || !err) return;

		function setError(msg) {
			err.textContent = msg;
			err.hidden = false;
			input.classList.add("is-error");
			input.setAttribute("aria-invalid", "true");
		}
		function clearError() {
			err.hidden = true;
			input.classList.remove("is-error");
			input.removeAttribute("aria-invalid");
		}

		function submit() {
			var z = pickZip(input.value);
			if (!z) {
				setError("Please enter a valid 5-digit ZIP code.");
				input.focus();
				return;
			}
			clearError();
			wrap.removeAttribute("data-dirty");

			// success / failed screen of the popup; the widget itself stays on the input box
			if (popup) popup.openWithZip(z, btn);
			else commitZip(z); // no popup on this page: still save + update labels
		}

		input.addEventListener("input", function (e) {
			e.stopPropagation();
			input.value = input.value.replace(/\D/g, "").slice(0, 5);
			if (!err.hidden && input.value.length === 5) clearError();
			// typing a different ZIP turns the box back to blue until "Check" is pressed again
			wrap.toggleAttribute("data-dirty", input.value !== (state.zip || ""));
		});
		input.addEventListener("change", function (e) { e.stopPropagation(); });
		input.addEventListener("keydown", function (e) {
			if (e.key === "Enter") {
				e.preventDefault(); // don't submit a surrounding filter form
				e.stopPropagation();
				submit();
			}
		});

		btn.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			submit();
		});
	}

	function scanZipChecks(scope) {
		scope = scope || document;
		var found = [];
		if (scope.matches && scope.matches(ZC_SEL)) found.push(scope);
		if (scope.querySelectorAll) scope.querySelectorAll(ZC_SEL).forEach(function (w) { found.push(w); });

		found.forEach(function (wrap) {
			if (wrap.__ldZc) return;      // already set up: leave whatever the customer typed alone
			enhanceZipCheck(wrap);
			syncZipInputs(wrap);          // prefill only this new copy with the current ZIP
		});
	}

	function watchZipChecks() {
		new MutationObserver(function (mutations) {
			mutations.forEach(function (m) {
				m.addedNodes.forEach(function (n) { if (n.nodeType === 1) scanZipChecks(n); });
			});
		}).observe(document.documentElement, { childList: true, subtree: true });
	}

	// Delegated: works for labels that are added to the page at any time
	document.addEventListener("click", function (e) {
		var t = e.target.closest && e.target.closest("[data-ld-open]");
		if (!t || !popup) return;
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
		popup.open(t);
	}, true);

	/* ---------------------------------------------------------------------
	 * Free Shipping filter flag  (<html data-ld-ship-filter>)
	 * ------------------------------------------------------------------- */
	function updateShipFilterFlag() {
		var shipActive = false, localActive = false;
		try {
			var sp = new URL(window.location.href).searchParams;
			SHIP_FILTER_PARAMS.forEach(function (p) {
				sp.getAll(p).forEach(function (v) {
					if (SHIP_FILTER_VALUES.indexOf(v) !== -1) shipActive = true;
					if (v === FILTER_VALUE) localActive = true;
				});
			});
		} catch (e) { /* ignore */ }
		// Free Shipping filter => keep "Free Shipping", UNLESS the Local Delivery filter is applied too
		// (Free Local Delivery is prioritized when both are applied)
		root.toggleAttribute("data-ld-ship-filter", shipActive && !localActive);
	}

	// Filters usually change the URL with history.pushState / replaceState (no page load), so watch those too
	["pushState", "replaceState"].forEach(function (m) {
		var orig = window.history[m];
		if (typeof orig !== "function") return;
		window.history[m] = function () {
			var r = orig.apply(this, arguments);
			updateShipFilterFlag();
			return r;
		};
	});
	window.addEventListener("popstate", updateShipFilterFlag);
	updateShipFilterFlag();

	/* ---------------------------------------------------------------------
	 * Boot
	 * ------------------------------------------------------------------- */
	function boot() {
		setupPopup();
		scanZipChecks(document);
		watchZipChecks();
		initState();
	}

	window.__bmLocalDelivery = {
		open: function () { if (popup) popup.open(); },
		close: function () { if (popup) popup.close(); },
		getState: function () { return { zip: state.zip, eligible: state.eligible, source: state.source }; },
		// Validates, saves and applies a ZIP typed by the customer. Returns { valid, zip, eligible }.
		setZip: commitZip,
		getStrippedFilterUrl: getStrippedFilterUrl,
		// Re-scan for inline ZIP widgets (only needed if you build one outside the normal DOM flow)
		scanZipChecks: function () { scanZipChecks(document); },
	};

	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
	else boot();
})();